/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { type OnInit, Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import {
    type ColumnDef, type Command, type HttpResponse,
    type Rest, type TableConfig, DMValidators, FormControl,
    FormGroup, FormSlideWindowAbstract, TransferTableComponent, UtilService,
} from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { FileSystemModeE } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { SmartGuiService, ToolService } from 'app/common/services';
import { type CIFSClient, type CIFSShare, type FileSystem, type LocalUserOrGroup } from 'app/common/types/mo';
import { ShardSwitch, SWITCH } from 'app/config/switch';
import { CreateUnixLocalAccessGroupComponent } from 'app/feature/service/file-service/access-user/unix-access-user/unix-local-access-group/create-unix-local-access-group/create-unix-local-access-group.component';
import { CreateUnixLocalAccessUserComponent } from 'app/feature/service/file-service/access-user/unix-access-user/unix-local-access-user/create-unix-local-access-user/create-unix-local-access-user.component';
import { CreateWindowsLocalAccessGroupComponent } from 'app/feature/service/file-service/access-user/windows-access-user/windows-local-access-group/create-windows-local-access-group/create-windows-local-access-group.component';
import { CreateWindowsLocalAccessUserComponent } from 'app/feature/service/file-service/access-user/windows-access-user/windows-local-access-user/create-windows-local-access-user/create-windows-local-access-user.component';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';
import { type Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { type SmartguiCifsshareReceived } from '../../../share';
import { ShareTypeE } from '../../../share.enum';
import { CifsShareService } from '../../cifs-share.service';
import { CifsShareStrService } from '../../cifs-share-str.service';
import { ClientCifsShareService } from '../client-cifs-share.service';
import { DomainTypeE, prefixHandler, UserTypeE } from '../client-clifs-share';

@Component({
    selector: 'app-add-user-or-group',
    templateUrl: './add-user-or-group.component.html',
    styleUrls: ['./add-user-or-group.component.scss'],
})
export class AddUserOrGroupComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 前台表格传入true
     */
    @Input() isFrontTable = false;
    @Input() vstoreId: string;
    @Input() selectedVstore: SelectOption;
    // 外部已选择的用户/用户组
    @Input() clients: CIFSClient[] = [];
    @Input() entry: FileSystemEntryE;
    /**
     * 后台表格传入的当前创建的client所属的share
     */
    @Input() cifsShare: CIFSShare;
    /**
     * 是否支持GUI，默认支持，create as和创审计日志文件系统的copyfrom下不支持。
     */
    @Input() supportSmartGui = true;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;
    @Output() callBack: EventEmitter<CIFSClient[]> = new EventEmitter();
    @ViewChild('transferTable') transferTable: TransferTableComponent;

    i18n = this.str.i18n;
    winTitle: string;
    loading = false;
    /** 高级选项 */
    advance = false;

    ShardSwitch = ShardSwitch;
    SWITCH = SWITCH;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    /**
     * 智能GUI载入中
     */
    smartGuiLoading = false;

    belongType = this.i18n.label.WindowsUserLab;
    userTypeOpts: SelectOption[];
    UserTypeE = UserTypeE;
    FileSystemEntryE = FileSystemEntryE;
    LicenseKey = LicenseKey;

    get userType(): UserTypeE {
        // 智能GUI获取到推荐数据前不给默认用户类型，不显示用户选择推拉表格，避免不必要的请求
        if (this.smartGuiLoading) {
            return undefined;
        }
        return this.vm ? this.vm.value.userType : UserTypeE.User;
    }

    /**
     * 判断是否为本地用户或用户组
     */
    get isLocal(): boolean {
        return this.str.localUsrAndGrp.includes(this.vm?.value.userType);
    }

    /**
     * 判断是否为域用户/用户组（不是本地用户/用户组，则为域用户/用户组）
     */
    get isDomain(): boolean {
        return !this.isLocal && this.vm?.value.userType !== UserTypeE.Everyone;
    }

    localRest: Rest;
    localSelection: LocalUserOrGroup[] = [];
    tableConfig: TableConfig;
    tableColumns: ColumnDef[];

    /**
     * 保存上一次输入的域名称
     */
    domainNames: Map<UserTypeE, string[]> = new Map();
    /** 域用户/用户组名称tooltip */
    domainToolTip: string;

    vm: FormGroup;
    errorMessage: any;

    showErrorMsg = false;
    showGuideTip = false;

    get guideHide(): boolean {
        return this.str.localUsrAndGrp.includes(this.userType) || this.userType === UserTypeE.Everyone;
    }

    configGuide = this.str.configGuideMap[UserTypeE.AD];

    /**
     * 权限级别选项，默认是完整的
     */
    permissionLevelOpts: SelectOption[] = this.str.permissionLevelOpts;

    constructor(
        injector: Injector,
        public str: CifsShareStrService,
        private service: CifsShareService,
        private toolService: ToolService,
        private clientService: ClientCifsShareService,
        public utilService: UtilService,
        private smartguiService: SmartGuiService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        let auditFsOpenWriteSwitch = false;

        if (this.fileSystem) {
            auditFsOpenWriteSwitch = this.fileSystem.isAuditLogFs && this.fileSystem.auditLogWritable;
        }

        this.handelUsertypeOpts(this.advance);
        this.initTable();

        // 表单初始化前就决定状态，确保开启智能GUI的情况初始不会有用户类型
        this.smartGuiLoading = this.supportSmartGui && this.smartguiService.switch;

        super.ngOnInit();

        if (!this.isFrontTable) {
            this.vstoreId = this.cifsShare.vstoreId;
        }
        this.localRest = this.service.getWindowsUserRest(this.vstoreId);

        // 审计日志文件系统下的CIFS共享添加用户/用户组的权限有限制
        if (this.entry === FileSystemEntryE.AuditLog && !auditFsOpenWriteSwitch) {
            this.permissionLevelOpts = this.str.auditLogFsPermissionLevelOpts;
        }

        this.windowVisible = true;
        this.pageReady = true;

        // 直接从共享添加用户/用户组，entry为undefined, 需要先查是不是属于审计日志文件系统
        // 支持根目录的需求DM暂时未做，这里先规避没有文件系统的情况
        const shouldGetFileSystem = this.cifsShare?.FSID &&
            this.cifsShare?.FSID !== '--' &&
            this.cifsShare?.subType !== ShareTypeE.Homedir &&
            !this.entry;
        if (shouldGetFileSystem) {
            // 审计日志文件系统、哈希文件系统和内部文件系统创共享要限制权限, Homedir不查询
            this.loading = true;
            this.service.getFileSystem(this.cifsShare.FSID, this.cifsShare.vstoreId)
                .subscribe((data) => {
                    if (!data) {
                        this.loading = false;
                        return;
                    }

                    const isNotSupportWriteAuditFs = data.isAuditLogFs && !data.auditLogWritable;

                    if (isNotSupportWriteAuditFs ||
                        data.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                        data.SUBTYPE === FileSystemModeE.HASH) {
                        this.permissionLevelOpts = this.str.auditLogFsPermissionLevelOpts;
                    }
                    this.getCifsSmartguiData();
                }, () => {
                    this.loading = false;
                });
        } else {
            this.getCifsSmartguiData();
        }
    }

    /**
     * 类型UNIX、LDAP、NIS选项需要开启“高级”才能看到
     */
    handelUsertypeOpts(advance: boolean): void {
        const handledUserTypeOpts = [...this.str.userTypeOpts];
        this.userTypeOpts = advance ?
            handledUserTypeOpts :
            handledUserTypeOpts.filter((item) => this.str.baseType.includes(item.value));
    }

    /**
     * “高级”选项变更回调
     */
    advanceChanged(advance: boolean): void {
        this.handelUsertypeOpts(advance);

        // 选中UNIX、LDAP、NIS等类型并且关闭“高级”选项时，重新切换至windows用户
        if (!this.str.baseType.includes(this.userType)) {
            this.vm.get('userType').setValue(UserTypeE.User);
        }
    }

    initVm(): void {
        this.vm = new FormGroup({
            userType: new FormControl(this.userType, [
                this.alreadySelectedValidator,
            ]),
            names: new FormControl([], [
                DMValidators.multiInputValidator(true, [
                    DMValidators.rangeByteLength(1, 256),
                    this.cifsAdNameValidator(),
                    this.repeatValidator(),
                ]),
            ]),
            permission: new FormControl(0, []),
        }, { updateOn: 'blur' });

        // 防止刚进入页面，选择windows用户后，无法下发（因为names校验不通过）
        this.checkNameStatus();

        this.errorMessage = {
            userType: {
                alreadySelected: this.i18n.error.alreadySelected,
            },
            names: {
                required: this.toolService.requiredErr(this.i18n.label.name),
                rangeByteLength: this.str.rangeByteOneLengthErr(1, 256),
                adName: this.i18n.error.adUserValidation,
                adGroupName: this.i18n.error.adGroupValidation,
                nameRepeat: this.str.repeatErr(this.i18n.label.name),
                otherName: this.i18n.error.otherDomainUsrValidation,
            },
        };

        this.vm.get('userType').valueChanges.subscribe((value: UserTypeE) => {
            const lastValue: UserTypeE = this.vm.value.userType;
            if (lastValue === value) {
                return;
            }

            // 更新表格配置与Rest
            if (this.str.localUsrAndGrp.includes(value)) {
                this.updateTabelConfig(value);
                this.getUserAndGroup(value);
            } else {
                this.getNamesTooltip(value);
            }
            this.localSelection = [];

            // 判断域是否配置
            if (!this.str.localUsrAndGrp.includes(value)) {
                this.configGuide = this.str.configGuideMap[value];
            }

            this.checkNameStatus(lastValue, value);
        });
    }

    /**
     * 更新域用户/用户组输入框的tooltip
     * AD域需要写域名，LDAP域和NIS域不需要写域名
     */
    getNamesTooltip(type: UserTypeE): void {
        const isAd = [UserTypeE.AD, UserTypeE.ADGroup].includes(type);
        if (isAd) {
            this.domainToolTip = type === UserTypeE.AD ? this.i18n.tip.adMultiNameTip : this.i18n.tip.adGroupMultiNameTip;
        } else {
            const isUsr = this.str.domainUsr.includes(type);
            this.domainToolTip = this.str.otherDomainValidation(isUsr, 1, 256);
        }
    }

    /**
     * windows和unix的id与name不同，windows全大写，unix全小写
     */
    updateTabelConfig(type: UserTypeE): void {
        // 清除表格过滤项
        this.transferTable?.leftTable.onClearFilterLabel();
        this.transferTable?.rightTable.onClearFilterLabel();

        const isWindows = [UserTypeE.User, UserTypeE.UserGroup].includes(type);

        this.tableColumns = [
            {
                header: this.i18n.label.name,
                field: isWindows ? 'NAME' : 'name',
                filterType: 'search',
                filterTrim: false,
            },
            {
                header: isWindows ? this.i18n.label.rid : this.i18n.label.id,
                field: isWindows ? 'RID' : 'id',
                // unix用户/用户组的id只支持精确搜索
                filterType: isWindows ? 'search' : 'equalSearch',
                sortable: true,
            },
        ];
    }

    /**
     * 设置本地用户和用户组的rest
     */
    getUserAndGroup(type: UserTypeE): void {
        if ([UserTypeE.User, UserTypeE.UnixUser].includes(type)) {
            const isWindowsUsr = type === UserTypeE.User;
            this.localRest = isWindowsUsr ?
                this.service.getWindowsUserRest(this.vstoreId) :
                this.service.getUnixUserRest(this.vstoreId);
            return;
        }

        if ([UserTypeE.UserGroup, UserTypeE.UnixUserGroup].includes(type)) {
            const isWindowsGrp = type === UserTypeE.UserGroup;
            this.localRest = isWindowsGrp ?
                this.service.getWindowsUserGroupRest(this.vstoreId) :
                this.service.getUnixUserGroupRest(this.vstoreId);

            // 只有windows用户组显示“类型”
            if (type === UserTypeE.UserGroup) {
                this.tableColumns.push({
                    field: 'GROUPTYPE',
                    columnFilter: true,
                    header: this.i18n.label.type,
                    filterType: 'dropdown',
                    options: this.str.groupTypeOpt,
                    render: (value: string) => this.utilService.getLabelFromOptions(this.str.groupTypeOpt, value),
                });
            }

            return;
        }
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'RID',
            selectionMode: 'checkbox',
            checkRowDisabled: (data: LocalUserOrGroup) => ({ disabled: this.selectedDisabled(data) }),
            beforeShow: (data: LocalUserOrGroup[]) => this.addRid(data),
        };

        this.tableColumns = [
            {
                header: this.i18n.label.name,
                field: 'NAME',
                filterType: 'search',
            },
            {
                header: this.i18n.label.rid,
                field: 'RID',
                filterType: 'search',
                sortable: true,
            },
        ];
    }

    /**
     * unix用户/用户组无RID，给其增加一个虚拟的RID当做dataKey
     */
    addRid(data: LocalUserOrGroup[]): LocalUserOrGroup[] {
        if ([UserTypeE.UnixUser, UserTypeE.UnixUserGroup].includes(this.userType)) {
            data.forEach((item) => {
                item.RID = item.id;
            });
        }
        return data;
    }

    /**
     * 用户类型切换时，将当前“名称”值保存，将上一次保存的值取出
     */
    checkNameStatus(lastType?: UserTypeE, newType?: UserTypeE): void {
        const names = this.vm.value.names;
        const domainList = [...this.str.domainUsr, ...this.str.domainGrp];

        if (domainList.includes(lastType)) {
            this.domainNames.set(lastType, names);
        }

        let restoreNames = [];
        if (domainList.includes(newType) && this.domainNames.has(newType)) {
            restoreNames = this.domainNames.get(newType);
        }
        this.vm.patchValue({ names: restoreNames });

        // 非域类型的时候禁域名称输入框,避免校验
        if (this.isDomain) {
            this.vm.get('names').enable();
        } else {
            this.vm.get('names').disable();
        }
    }

    /**
     * 获取对应的域类型
     */
    domainType(): DomainTypeE {
        switch (this.userType) {
            case UserTypeE.Everyone: return DomainTypeE.Local;
            case UserTypeE.User: return DomainTypeE.Local;
            case UserTypeE.UserGroup: return DomainTypeE.Local;
            case UserTypeE.AD: return DomainTypeE.AD;
            case UserTypeE.ADGroup: return DomainTypeE.AD;
            case UserTypeE.UnixUser: return DomainTypeE.Unix;
            case UserTypeE.UnixUserGroup: return DomainTypeE.Unix;
            case UserTypeE.Ldap: return DomainTypeE.Ldap;
            case UserTypeE.LdapGroup: return DomainTypeE.Ldap;
            case UserTypeE.Nis: return DomainTypeE.Nis;
            case UserTypeE.NisGroup: return DomainTypeE.Nis;
            default:
                return undefined;
        }
    }

    onSelectionChange(): void {
        // 选项变化时隐藏错误提示
        this.showErrorMsg = this.localSelection.length === 0;
    }

    /**
     * 是否禁用推拉表格里已经选择过的windows和unix用户/用户组
     */
    selectedDisabled(data: LocalUserOrGroup): boolean {
        const userType = this.vm.value.userType;
        const domainType = this.domainType();

        const isSelected = this.clients.some((item) => {
            // 接口查回来的client数据，没有userType，因此用domainType + NAME判断
            const isWindowsUsr = userType === UserTypeE.User && domainType === item.DOMAINTYPE && item.NAME === data.NAME;
            const isWindowsGrp = userType === UserTypeE.UserGroup && domainType === item.DOMAINTYPE && item.NAME === `@${data.NAME}`;
            const isUnixUsr = userType === UserTypeE.UnixUser && domainType === item.DOMAINTYPE && item.NAME === data.name;
            const isUnixGrp = userType === UserTypeE.UnixUserGroup && domainType === item.DOMAINTYPE && item.NAME === `@${data.name}`;

            return isWindowsUsr || isWindowsGrp || isUnixUsr || isUnixGrp;
        });

        return isSelected;
    }

    /**
     * 校验用户/用户组推拉表格
     */
    validateSelection(): boolean {
        if (this.isLocal) {
            if (!this.localSelection.length) {
                this.showErrorMsg = true;
                return false;
            }
        }
        return true;
    }

    // 不能跟列表中已有选项重复,只有前端表格才会校验，因为后端表格clients是个空数组，返回null
    repeatValidator() {
        return (ctrl: AbstractControl): { [key: string]: boolean } | null => {
            if (!ctrl.value) {
                return null;
            }
            // 之前的逻辑是批量校验，现在改成单个了，由于逻辑有点复杂，直接构造成数组去校验
            const valueArr = [ctrl.value];
            const clients = this.clients.filter((item) => item.userType === this.userType);
            for (let i = 0; i < clients.length; i++) {
                const clientObj = clients[i];
                // 如果是相同类型才比较
                if (
                    // 如果相等，那就一定是相等
                    valueArr.indexOf(clientObj.NAME) >= 0 ||
                    // 如果去掉@并且类型是net，那就重复
                    (valueArr.indexOf(prefixHandler(clientObj.NAME)) >= 0 && this.str.domainGrp.includes(Number(clientObj.userType)))) {
                    return { nameRepeat: true };
                }
            }
            return null;
        };
    }

    /**
     * 域用户/用户组名称校验
     */
    cifsAdNameValidator = () => (control: AbstractControl): ValidationErrors | null => {
        const isAd = [UserTypeE.AD, UserTypeE.ADGroup].includes(this.userType);

        // AD域需要加域名，LDAP和NIS不需要域名
        const reg = isAd ? /[^ \f\n\r\t\v\\]+\\[^ \f\n\r\t\v\\]+/ : /[^ \f\n\r\t\v\\]+/;
        const isUsr = this.str.domainUsr.includes(this.userType);
        let regValid = reg.test(control.value);

        // 域用户名不能以@开头
        if (isUsr && control.value.startsWith('@')) {
            regValid = false;
        }

        let errorKey: string;
        if (isAd) {
            errorKey = isUsr ? 'adName' : 'adGroupName';
        } else {
            errorKey = isUsr ? 'otherName' : '';
        }

        return regValid ? null : { [errorKey]: true };
    };

    /**
     * 用户类型校验
     */
    alreadySelectedValidator = (control: AbstractControl): ValidationErrors | null => {
        const userTypeFormCtl = control.root.get('userType') as FormControl;
        if (!userTypeFormCtl) {
            return null;
        }
        userTypeFormCtl.markAsTouched();
        // 当外部已选Everyone时，再选择Everyone校验不通过
        const invalid = control.value === UserTypeE.Everyone &&
            this.clients.some((item) => item.NAME === '@Everyone' && Number(item.DOMAINTYPE) === DomainTypeE.Local);
        return invalid ? { alreadySelected: true } : null;
    };

    collectionClients(): CIFSClient[] {
        let data: CIFSClient[] = [];
        const names: string[] = this.vm.value.names;
        const permission: number = this.vm.value.permission;
        const map = new Map([
            [UserTypeE.Everyone, (): void => {
                // 用户类型为Everyone时类型为windows本地认证用户/组, Everyone其实是windows本地认证用户组中的一项
                data = [{
                    ID: `Everyone-${UserTypeE.Everyone}`,
                    NAME: '@Everyone',
                    DOMAINTYPE: DomainTypeE.Local,
                }];
            }],
            [UserTypeE.User, (): void => {
                data = this.localSelection.map((item: LocalUserOrGroup) => ({
                    ID: `${item.RID}-${UserTypeE.User}`,
                    NAME: item.NAME,
                    DOMAINTYPE: DomainTypeE.Local,
                    RID: item.RID,
                }));
            }],
            [UserTypeE.UserGroup, (): void => {
                data = this.localSelection.map((item: LocalUserOrGroup) => ({
                    ID: `${item.RID}-${UserTypeE.UserGroup}`,
                    NAME: `@${item.NAME}`,
                    DOMAINTYPE: DomainTypeE.Local,
                    RID: item.RID,
                }));
            }],
            [UserTypeE.UnixUser, (): void => {
                data = this.localSelection.map((item: LocalUserOrGroup) => ({
                    ID: `${item.id}-${UserTypeE.UnixUser}`,
                    NAME: item.name,
                    DOMAINTYPE: DomainTypeE.Unix,
                }));
            }],
            [UserTypeE.UnixUserGroup, (): void => {
                data = this.localSelection.map((item: LocalUserOrGroup) => ({
                    ID: `${item.id}-${UserTypeE.UnixUserGroup}`,
                    NAME: `@${item.name}`,
                    DOMAINTYPE: DomainTypeE.Unix,
                }));
            }],
            [UserTypeE.AD, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.AD}`,
                    NAME: item,
                    DOMAINTYPE: DomainTypeE.AD,
                }));
            }],
            [UserTypeE.ADGroup, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.ADGroup}`,
                    NAME: `@${item}`,
                    DOMAINTYPE: DomainTypeE.AD,
                }));
            }],
            [UserTypeE.Ldap, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.Ldap}`,
                    NAME: item,
                    DOMAINTYPE: DomainTypeE.Ldap,
                }));
            }],
            [UserTypeE.LdapGroup, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.LdapGroup}`,
                    NAME: `@${item}`,
                    DOMAINTYPE: DomainTypeE.Ldap,
                }));
            }],
            [UserTypeE.Nis, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.Nis}`,
                    NAME: item,
                    DOMAINTYPE: DomainTypeE.Nis,
                }));
            }],
            [UserTypeE.NisGroup, (): void => {
                data = names.map((item: string) => ({
                    ID: `${item}-${UserTypeE.NisGroup}`,
                    NAME: `@${item}`,
                    DOMAINTYPE: DomainTypeE.Nis,
                }));
            }],
        ]);
        map.get(this.userType)();
        data = data.map((item: CIFSClient) => {
            item.PERMISSION = permission;
            // 加上自定义的用户类型
            item.userType = this.userType;
            return item;
        });

        return data;
    }

    isModified(): boolean {
        return true;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];
        // 前台表格不需要cmds，只用emit数据回去
        // 先收集数据
        const datas = this.collectionClients();
        if (this.isFrontTable) {
            this.callBack.emit(datas);
        } else {
            return this.buildCIFSClientCmds(datas);
        }
        return cmds;
    }

    /**
     * 构建添加客户端的rest
     */
    buildCIFSClientCmds(datas: CIFSClient[]): Command[] {
        const cmds: Command[] = [];
        const params: any = {};
        params.auth = datas;
        params.vstoreId = this.cifsShare.vstoreId;
        params.cifsID = this.cifsShare.ID;

        cmds.push({
            name: this.i18n.title.addCifsPermission,
            action: this.clientService.addClients(this.cifsShare.NAME, params),
        });

        return cmds;
    }

    done(): void {
        // 先校验推拉表格
        if (!this.validateSelection()) {
            return;
        }
        this.onOk();
    }

    /**
     * 获取CIFS智能推荐数据
     */
    getCifsSmartguiData(): void {
        if (!this.smartguiService.switch || !this.supportSmartGui) {
            this.loading = false;
            return;
        }

        const cifsClientTableHasEveryone = this.clients.some(
            (item) => item.NAME === '@Everyone' && Number(item.DOMAINTYPE) === DomainTypeE.Local,
        );

        // 添加忙等效果
        this.showLoading();
        this.clientService.getSmartguiCIFS(this.vstoreId, { requestTimeoutTip: this.i18n.error.smartRecommendDataTimeoutMsg })
            .pipe(finalize(() => {
                this.hideLoading();
                this.smartGuiLoading = false;
            }))
            .subscribe((data: SmartguiCifsshareReceived) => {
                if (!data) {
                    return;
                }

                const userType = this.str.getUserType(data) ?? UserTypeE.Everyone;
                const isRecommendEveryone = userType === UserTypeE.Everyone;
                // 外部表格数据包含Everyone且GUI推荐Everyone则设置默认用户类型，否则设置推荐类型
                if (isRecommendEveryone && cifsClientTableHasEveryone) {
                    // 设置默认的用户类型
                    const userTypeItem = this.str.userTypeOpts.find((item) => item.value === UserTypeE.User);
                    this.vm.get('userType').setValue(userTypeItem.value);
                } else {
                    // 设置推荐的用户类型
                    if (!this.str.baseType.includes(userType)) {
                        // 如果推荐的类型在高级中，需要打开高级选项
                        this.advance = true;
                        this.advanceChanged(true);
                    }
                    const userTypeItem = this.str.userTypeOpts.find((item) => item.value === userType);
                    this.vm.get('userType').setValue(userTypeItem.value);
                }

                // 设置推荐的权限级别
                const permissionLevelItem = this.permissionLevelOpts.find((item) => item.value === Number(data.permission));
                // 推荐权限不存在时不执行操作，保持默认权限
                if (permissionLevelItem) {
                    this.vm.get('permission').setValue(permissionLevelItem.value);
                }
            }, () => {
                // 获取智能GUI数据失败，给用户类型一个值
                this.smartGuiLoading = false;

                // 有用户类型就推荐用户类型，否则根据是否有everyone推荐
                const userTypeItem = this.userType ?? (cifsClientTableHasEveryone ? UserTypeE.User : UserTypeE.Everyone);
                this.vm.get('userType').setValue(userTypeItem);
            });
    }

    /**
     * 创建local用户/用户组
     */
    onCreate(): void {
        const userType = this.vm.value.userType;
        const actionOpts = {
            vstore: this.selectedVstore,
            onClose: ({ data }): void => {
                this.refreshAndUpdate(data);
            },
        };
        if (userType === UserTypeE.User) {
            this.doAction(CreateWindowsLocalAccessUserComponent, actionOpts);
            return;
        }

        if (userType === UserTypeE.UserGroup) {
            this.doAction(CreateWindowsLocalAccessGroupComponent, actionOpts);
            return;
        }

        if (userType === UserTypeE.UnixUser) {
            this.doAction(CreateUnixLocalAccessUserComponent, actionOpts);
            return;
        }

        if (userType === UserTypeE.UnixUserGroup) {
            this.doAction(CreateUnixLocalAccessGroupComponent, actionOpts);
            return;
        }
    }

    /**
     * 创建本地用户/用户组后，更新表格
     */
    refreshAndUpdate(data: LocalUserOrGroup[]): void {
        this.transferTable.leftTable.refresh();
        if (!data || !data.length) {
            this.localSelection = [...this.localSelection, ...data];
        }

        // 清除校验
        this.showErrorMsg = false;
        this.transferTable.rightTable.refresh();
    }

    /**
     * 复写exec，创建omtask任务成功不弹浮窗
     */
    exec(): void {
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                this.loading = false;
            })).subscribe(() => {
                this.closeArgs.switchToPermissionList = true;
                this.closeArgs.refresh = true;
                this.hide();
            });
        } else {
            this.hide();
        }
    }
}

